// src\app\core\services\theme.service.ts
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly STORAGE_KEY = 'darkModeEnabled';
  private _darkMode: BehaviorSubject<boolean>;

  public isDarkMode$: Observable<boolean>;

  constructor() {
    // 1. Tenta ler a preferência salva (localStorage).
    const savedTheme = localStorage.getItem(this.STORAGE_KEY);

    // 2. MODIFICAÇÃO: Se não houver preferência salva, o padrão é CLARO (false).
    // Ignoramos o sistema para não afetar o fluxo de registro.
    const initialIsDark = savedTheme === 'true';

    // Inicializa o BehaviorSubject com o estado determinado
    this._darkMode = new BehaviorSubject<boolean>(initialIsDark);
    this.isDarkMode$ = this._darkMode.asObservable();

    // 3. Aplica o tema inicial
    this.applyGlobalThemeClass(initialIsDark);
  }

  /**
   * Força o tema claro e remove a preferência salva.
   * Ideal para iniciar um novo fluxo de registro (Signup).
   */
  public resetToLightMode(): void {
    localStorage.removeItem(this.STORAGE_KEY);
    this.toggleDarkMode(false);
  }

  /**
   * Método principal para alternar o tema.
   * @param isDark Define se o modo escuro deve ser ativado ou desativado.
   */
  public toggleDarkMode(isDark: boolean): void {
    // 1. Aplica a classe globalmente no DOM
    this.applyGlobalThemeClass(isDark);

    // 2. Notifica todos os subscribers reativos (como o SettingsComponent)
    this._darkMode.next(isDark);

    // 3. Salva a preferência para persistir entre recarregamentos
    localStorage.setItem(this.STORAGE_KEY, String(isDark));
  }

  /**
   * Força um tema apenas visualmente, sem salvar no localStorage.
   * Útil para telas que possuem design fixo (ex: Signin/Signup sempre escuros).
   */
  public forceThemeTemporary(isDark: boolean): void {
    this.applyGlobalThemeClass(isDark);
    // Notifica componentes que dependem do observável, mas não altera o disco
    this._darkMode.next(isDark);
  }

  /**
   * Manipula diretamente a classe no body do documento.
   */
  private applyGlobalThemeClass(isDark: boolean): void {
    // Adiciona ou remove a classe 'dark-mode' baseada no booleano.
    // Certifique-se de que seu variables.scss usa body.dark-mode para as variáveis.
    if (isDark) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }

    // Sincroniza a cor da interface do navegador com o tema do MOVFASCIA.
    const themeColor = isDark ? '#3D474D' : '#FFFFFF';
    const themeColorMeta = document.querySelector<HTMLMetaElement>(
      'meta[name="theme-color"]'
    );

    if (themeColorMeta) {
      themeColorMeta.setAttribute('content', themeColor);
    }
  }

  /**
   * Getter para acesso síncrono e imediato ao estado atual.
   */
  public get isDarkMode(): boolean {
    return this._darkMode.value;
  }
}
